import type { BlockResponse, ExpertReportResponse, PointsRuleIssue, PointsRuleResponse } from '../api/types';

/**
 * Формула правила расчёта по умолчанию — сумма баллов вложенных блоков.
 *
 * Её же сервер выдаёт блоку, ставшему родительским, и заключению без правила
 * (контракт `PUT /expert-report/{id}`). Правило по умолчанию к вложенным блокам по названиям
 * не обращается, поэтому их переименование и добавление ему не вредят.
 */
export const DEFAULT_POINTS_RULE_DSL = 'SUM(children)';

/**
 * Код проблемы правила, который остаётся предупреждением: правило с ним применяется.
 * Остальные коды делают правило неприменимым (контракт `ExpertReportPointsRuleService`).
 */
const WARNING_POINTS_RULE_ISSUE_CODE = 'UnknownValue';

/** Правило отличается от правила по умолчанию — блок оценивается особым условием */
export const isCustomPointsRule = (pointsRule: PointsRuleResponse | null | undefined): boolean => {
  const dsl = pointsRule?.dsl?.trim();
  if (!dsl) return false;
  return dsl !== DEFAULT_POINTS_RULE_DSL;
};

/** Правило нельзя применить: среди его проблем есть не только предупреждения */
export const isPointsRuleInapplicable = (issues: PointsRuleIssue[] | null | undefined): boolean =>
  Boolean(issues?.some((issue) => issue.code !== WARNING_POINTS_RULE_ISSUE_CODE));

/** Ссылка на вложенный блок в формуле — его название в квадратных скобках */
const BLOCK_REFERENCE_PATTERN = /\[([^\]]*)\]/g;

/** Набор результатов всех вложенных блоков в формуле */
const ALL_CHILDREN_PATTERN = /\bchildren\b/;

/**
 * Почему правило расчёта требует внимания пользователя:
 * - `inapplicable` — правило нельзя применить, результат считается правилом по умолчанию;
 * - `missingChildren` — правило считает результат не по всем вложенным блокам.
 */
export type PointsRuleAttention = 'inapplicable' | 'missingChildren';

/** Владелец правила расчёта: блок с вложенными блоками либо само заключение */
export type PointsRuleOwner = {
  name: string;
  pointsRule: PointsRuleResponse | null;
  /** Названия прямых вложенных блоков, по результатам которых правило считает свой результат */
  childNames: string[];
};

/**
 * Требует ли правило расчёта владельца внимания пользователя; `null` — не требует.
 *
 * Особое правило обращается к вложенным блокам по названиям и само за ними не меняется, поэтому
 * правка вложенных блоков может оставить его неприменимым или считающим результат не по всему
 * их составу. Правило по умолчанию берёт все вложенные блоки разом и внимания не требует никогда.
 */
export const getPointsRuleAttention = (owner: PointsRuleOwner): PointsRuleAttention | null => {
  if (isPointsRuleInapplicable(owner.pointsRule?.issues)) return 'inapplicable';
  if (!isCustomPointsRule(owner.pointsRule)) return null;

  const dsl = owner.pointsRule?.dsl ?? '';
  if (ALL_CHILDREN_PATTERN.test(dsl)) return null;

  const referenced = new Set(Array.from(dsl.matchAll(BLOCK_REFERENCE_PATTERN), (match) => match[1].trim()));
  return owner.childNames.every((name) => referenced.has(name)) ? null : 'missingChildren';
};

/** Названия прямых вложенных блоков, участвующих в правиле расчёта их владельца */
const getChildNames = (children: BlockResponse[] | undefined): string[] =>
  (children ?? []).filter((child) => !child.isDeleted).map((child) => child.name);

/** Заключение как владелец правила расчёта своего итога */
export const reportAsPointsRuleOwner = (report: ExpertReportResponse): PointsRuleOwner => ({
  name: report.name,
  pointsRule: report.pointsRule,
  childNames: getChildNames(report.blocks),
});

/** Блок с вложенными блоками как владелец правила расчёта своего результата */
export const blockAsPointsRuleOwner = (block: BlockResponse): PointsRuleOwner => ({
  name: block.name,
  pointsRule: block.pointsRule,
  childNames: getChildNames(block.children),
});

/**
 * Требует ли внимания правило расчёта хотя бы одного блока внутри поддерева; `null` — не требует.
 *
 * Нужна свёрнутому узлу: пока он не раскрыт, отметки самих блоков не видны, и расхождение правила
 * с деревом заметил бы только тот, кто раскрыл дерево целиком. Неприменимое правило важнее
 * считающего результат не по всему составу, поэтому при обеих причинах называется оно.
 */
export const findNestedPointsRuleAttention = (blocks: BlockResponse[] | undefined): PointsRuleAttention | null => {
  let found: PointsRuleAttention | null = null;

  for (const block of blocks ?? []) {
    if (block.isDeleted) continue;

    const own = getPointsRuleAttention(blockAsPointsRuleOwner(block));
    if (own === 'inapplicable') return own;

    const nested = findNestedPointsRuleAttention(block.children);
    if (nested === 'inapplicable') return nested;

    found = found ?? own ?? nested;
  }

  return found;
};

/**
 * Вышестоящий блок указанного блока; `undefined` — блок верхнего уровня либо его нет в заключении.
 */
export const findParentBlockId = (report: ExpertReportResponse, blockId: number): number | undefined => {
  const findParent = (blocks: BlockResponse[], parent: BlockResponse | null): BlockResponse | null | undefined => {
    for (const block of blocks) {
      if (block.id === blockId) return parent;
      const found = findParent(block.children ?? [], block);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  return findParent(report.blocks, null)?.id;
};

/**
 * Владелец правила расчёта, в которое входят вложенные блоки указанного блока: сам блок,
 * а при его отсутствии (блок добавляется на верхний уровень) — заключение.
 */
export const findPointsRuleOwnerOfChildren = (
  report: ExpertReportResponse,
  parentBlockId: number | undefined,
): PointsRuleOwner => {
  if (parentBlockId === undefined) return reportAsPointsRuleOwner(report);

  const findBlock = (blocks: BlockResponse[]): BlockResponse | undefined => {
    for (const block of blocks) {
      if (block.id === parentBlockId) return block;
      const found = findBlock(block.children ?? []);
      if (found) return found;
    }
    return undefined;
  };

  const parent = findBlock(report.blocks);
  return parent ? blockAsPointsRuleOwner(parent) : reportAsPointsRuleOwner(report);
};

/**
 * Владелец правила расчёта, в которое входит блок: вышестоящий блок либо само заключение
 * (у блока верхнего уровня). Именно это правило нужно проверить, когда состав или названия
 * вложенных блоков изменились.
 */
export const findPointsRuleOwner = (report: ExpertReportResponse, blockId: number): PointsRuleOwner =>
  findPointsRuleOwnerOfChildren(report, findParentBlockId(report, blockId));
